import React, { useState, useEffect, useRef } from 'react';

// Helper to convert inches to feet and inches
const inchesToFeetInches = (inches) => {
  const feet = Math.floor(inches / 12);
  const remainingInches = Math.round(inches % 12);
  if (feet === 0) return `${remainingInches}"`;
  if (remainingInches === 0) return `${feet}'`;
  return `${feet}'${remainingInches}"`;
};

const ArtworkElement = ({ artwork, scaleX, scaleY, onRemove, onNudge, onEditDimensions, onShowMeasurements, getOptimizedImageUrl, audioMap, wallHeight, wallWidth }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [showNudge, setShowNudge] = useState(false);
  const nudgeRef = useRef(null);
  const hasAudio = audioMap && audioMap[artwork.vc_id];
  
  // Close nudge controls when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (nudgeRef.current && !nudgeRef.current.contains(e.target)) {
        setShowNudge(false);
      }
    };
    
    if (showNudge) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showNudge]);
  
  // Handle keyboard nudge when selected
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!showNudge || !onNudge) return;
      
      const nudgeAmount = e.shiftKey ? 0.25 : 1; // Hold shift for fine nudge (1/4 inch)
      
      switch (e.key) {
        case 'ArrowUp':
          e.preventDefault();
          onNudge(artwork.element_id, 0, -nudgeAmount);
          break;
        case 'ArrowDown':
          e.preventDefault();
          onNudge(artwork.element_id, 0, nudgeAmount);
          break;
        case 'ArrowLeft':
          e.preventDefault();
          onNudge(artwork.element_id, -nudgeAmount, 0);
          break;
        case 'ArrowRight':
          e.preventDefault();
          onNudge(artwork.element_id, nudgeAmount, 0);
          break;
        case 'Escape':
          setShowNudge(false);
          break;
        default:
          break;
      }
    };
    
    if (showNudge) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [showNudge, artwork.element_id, onNudge]);
  
  // Handle drag start
  const handleDragStart = (e) => {
    setIsDragging(true);
    setShowNudge(false);
    
    // Use lower quality for drag operation
    const imgEl = e.currentTarget.querySelector('img');
    if (imgEl && imgEl.dataset.originalSrc) {
      imgEl.src = getOptimizedImageUrl(imgEl.dataset.originalSrc, 'drag');
    }
    
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', artwork.element_id);
  };
  
  // Handle drag end
  const handleDragEnd = (e) => {
    setIsDragging(false);
    
    // Restore image quality
    const imgEl = e.currentTarget.querySelector('img');
    if (imgEl && imgEl.dataset.originalSrc) {
      imgEl.src = getOptimizedImageUrl(imgEl.dataset.originalSrc, 'display');
    }
  };
  
  // Handle click to show nudge controls
  const handleClick = (e) => {
    e.stopPropagation();
    if (!isDragging && onNudge) {
      setShowNudge(!showNudge);
    }
  };
  
  // Nudge button handler
  const handleNudgeClick = (dx, dy, e) => {
    e.stopPropagation();
    if (onNudge) {
      onNudge(artwork.element_id, dx, dy);
    }
  };


  
  // Position values - prefer wall_position if available, fall back to position_x/y
  const posX = artwork.wall_position_x !== undefined ? artwork.wall_position_x : 
              (artwork.position_x !== undefined ? artwork.position_x : 0);
  const posY = artwork.wall_position_y !== undefined ? artwork.wall_position_y : 
              (artwork.position_y !== undefined ? artwork.position_y : 0);
  
  return (
    <div 
      ref={nudgeRef}
      className={`artwork ${isDragging ? 'dragging' : ''} ${showNudge ? 'selected' : ''}`}
      id={`art-${artwork.element_id}`}
      data-id={artwork.element_id}
      data-vc-id={artwork.vc_id}
      data-pos-x={posX}
      data-pos-y={posY}
      draggable
      style={{
        left: `${posX * scaleX}px`,
        top: `${posY * scaleY}px`,
        width: `${artwork.width * scaleX}px`,
        height: `${artwork.height * scaleY}px`
      }}
      onClick={handleClick}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      {/* Artwork image with optimization */}
      <img 
        src={getOptimizedImageUrl(artwork.source, 'display')}
        alt={artwork.vc_id || `Artwork ${artwork.element_id}`}
        data-original-src={artwork.source}
      />
      
      {/* Nudge controls - appear when artwork is clicked */}
      {showNudge && onNudge && (
        <div className="nudge-controls">
          <button 
            className="nudge-btn nudge-up" 
            onClick={(e) => handleNudgeClick(0, -1, e)}
            title="Move up 1 inch (Shift+↑ for ¼ inch)"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 4l-8 8h5v8h6v-8h5z"/>
            </svg>
          </button>
          <button 
            className="nudge-btn nudge-down" 
            onClick={(e) => handleNudgeClick(0, 1, e)}
            title="Move down 1 inch (Shift+↓ for ¼ inch)"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 20l8-8h-5v-8h-6v8h-5z"/>
            </svg>
          </button>
          <button 
            className="nudge-btn nudge-left" 
            onClick={(e) => handleNudgeClick(-1, 0, e)}
            title="Move left 1 inch (Shift+← for ¼ inch)"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
              <path d="M4 12l8-8v5h8v6h-8v5z"/>
            </svg>
          </button>
          <button 
            className="nudge-btn nudge-right" 
            onClick={(e) => handleNudgeClick(1, 0, e)}
            title="Move right 1 inch (Shift+→ for ¼ inch)"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
              <path d="M20 12l-8 8v-5h-8v-6h8v-5z"/>
            </svg>
          </button>
          <div className="nudge-hint">Arrow keys • Shift = fine</div>
        </div>
      )}
      
      {/* Audio indicator */}
      {hasAudio && (
        <div className="artwork-audio-indicator" title="Has Audio Story">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M3 14v-4a9 9 0 0 1 18 0v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M21 14a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-2a2 2 0 0 1 2-2h3zM3 14a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-2a2 2 0 0 0-2-2H3z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
      )}
      
      {/* Dimensions tooltip - hide when nudge controls are shown */}
      {!showNudge && (
        <div className="info-tooltip">
          {artwork.title && <div style={{ fontWeight: '500', marginBottom: '2px' }}>{artwork.title}</div>}
          {artwork.width}" × {artwork.height}"
          {artwork.vc_id && ` | ${artwork.vc_id}`}
          {(wallHeight || wallWidth) && (() => {
            const posX = artwork.wall_position_x !== undefined ? artwork.wall_position_x : 
                        (artwork.position_x !== undefined ? artwork.position_x : 0);
            const posY = artwork.wall_position_y !== undefined ? artwork.wall_position_y : 
                        (artwork.position_y !== undefined ? artwork.position_y : 0);
            const bottomFromTop = posY + artwork.height;
            const heightFromFloor = wallHeight - bottomFromTop;
            const centerFromFloor = wallHeight - posY - (artwork.height / 2);
            
            // Calculate center of artwork on wall
            const artworkCenterX = posX + (artwork.width / 2);
            const wallCenterX = wallWidth / 2;
            const distanceFromWallCenter = artworkCenterX - wallCenterX;
            const distanceFromLeftEdge = artworkCenterX;
            const distanceFromRightEdge = wallWidth - artworkCenterX;
            
            // Get hanger height from artwork.more (stored in vcs.more jsonb)
            const hangerHeight = artwork.more?.hanger_height || artwork.hanger_height || null;
            // Hook placement = height from floor to bottom of artwork + artwork height - hanger height from top
            const hookPlacement = hangerHeight ? heightFromFloor + artwork.height - hangerHeight : null;
            
            return (
              <>
                <div style={{ fontSize: '10px', marginTop: '2px', opacity: 0.85 }}>
                  Floor: {inchesToFeetInches(heightFromFloor)} • Center: {inchesToFeetInches(centerFromFloor)}
                </div>
                {wallWidth && (
                  <div style={{ fontSize: '10px', marginTop: '1px', opacity: 0.85 }}>
                    L: {inchesToFeetInches(distanceFromLeftEdge)} • R: {inchesToFeetInches(distanceFromRightEdge)}
                  </div>
                )}
                {hookPlacement !== null && (
                  <div style={{ fontSize: '10px', marginTop: '1px', color: '#10b981', fontWeight: '500' }}>
                    🪝 Hook: {inchesToFeetInches(hookPlacement)} from floor
                  </div>
                )}
              </>
            );
          })()}
        </div>
      )}
      
      {/* Measurements button */}
      {onShowMeasurements && (
        <button 
          className="measurements-btn"
          title="View measurements & set hanger height"
          onClick={(e) => { e.stopPropagation(); onShowMeasurements(artwork); }}
        >
          📏
        </button>
      )}
      
      {/* Edit dimensions button */}
      {onEditDimensions && (
        <button 
          className="edit-dimensions-btn"
          title="Edit dimensions"
          onClick={(e) => { e.stopPropagation(); onEditDimensions(artwork); }}
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 3H3v18h18V3z"/>
            <path d="M9 3v18M3 15h18"/>
          </svg>
        </button>
      )}
      
      {/* Remove button */}
      <button 
        className="remove-btn"
        title="Remove from wall"
        onClick={(e) => { e.stopPropagation(); onRemove(artwork.element_id); }}
      >
        ×
      </button>
    </div>
  );
};

export default ArtworkElement;